import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import DeleteIcon from '@mui/icons-material/Delete';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import HistoryIcon from '@mui/icons-material/History';
import { Button, Divider, Grid, LinearProgress } from '@mui/material';
import { type FormEventHandler, type ReactNode, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import AppButton from './AppButton';
import AppDialog from './AppDialog';
import AppForm from './AppForm';

type EditFormProps = EditFormCommonProps & (EditFormInDialogProps | EditFormInPageProps);

interface EditFormCommonProps {
  module: string;
  title: string;
  children: ReactNode;
  loading?: boolean;
  readonly?: boolean;
  showActivate?: boolean;
  showDelete?: boolean;
  onSave: FormEventHandler<any>;
  onDelete?: () => void;
  onActivate?: () => void;
  onClear: () => void;
}

interface EditFormInDialogProps {
  onClose: () => void;
}

interface EditFormInPageProps {
  returnLink: string;
}

const EditForm = ({
  module,
  title,
  children,
  loading,
  readonly,
  showActivate,
  showDelete,
  onSave,
  onDelete,
  onActivate,
  onClear,
  ...otherProps
}: EditFormProps) => {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmActivate, setShowConfirmActivate] = useState(false);

  const alertForDeletion = () => {
    setShowConfirmDelete(true);
  };
  const confirmDeletion = () => {
    if (onDelete) onDelete();
    setShowConfirmDelete(false);
  };

  const alertForActivation = () => {
    setShowConfirmActivate(true);
  };
  const confirmActivation = () => {
    if (onActivate) onActivate();
    setShowConfirmActivate(false);
  };

  return (
    <>
      <AppForm title={title} onSubmit={onSave}>
        {children}
        <Grid>{loading ? <LinearProgress color="secondary" /> : <></>}</Grid>
        <Divider />
        <Grid>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid>
              <AppButton
                module={module}
                permission={showDelete ? 'actualizar' : 'agregar'}
                type="submit"
                variant="contained"
                color="secondary"
                startIcon={<CheckCircleIcon />}
                disabled={loading || readonly}
                disableElevation
              >
                Guardar
              </AppButton>
            </Grid>
            {showDelete ? (
              <Grid>
                <AppButton
                  module={module}
                  permission="eliminar"
                  variant="contained"
                  onClick={alertForDeletion}
                  startIcon={<DeleteIcon />}
                  disabled={loading || readonly}
                  disableElevation
                >
                  Eliminar
                </AppButton>
              </Grid>
            ) : (
              <></>
            )}
            {showActivate ? (
              <Grid>
                <AppButton
                  module={module}
                  permission="activar"
                  variant="contained"
                  onClick={alertForActivation}
                  startIcon={<HistoryIcon />}
                  disabled={loading || !readonly}
                  disableElevation
                >
                  Activar
                </AppButton>
              </Grid>
            ) : (
              <></>
            )}
            <Grid>
              <AppButton
                module={module}
                permission={showDelete ? 'actualizar' : 'agregar'}
                variant="contained"
                onClick={onClear}
                startIcon={showDelete ? <CancelIcon /> : <CleaningServicesIcon />}
                disabled={loading || readonly}
                disableElevation
              >
                {showDelete ? 'Cancelar cambios' : 'Limpiar'}
              </AppButton>
            </Grid>
            <Grid>
              {'returnLink' in otherProps ? (
                <Button
                  variant="contained"
                  startIcon={<ExitToAppIcon />}
                  component={RouterLink}
                  to={otherProps.returnLink}
                  disableElevation
                >
                  Salir
                </Button>
              ) : (
                <></>
              )}
              {'onClose' in otherProps ? (
                <Button
                  variant="contained"
                  startIcon={<ExitToAppIcon />}
                  onClick={otherProps.onClose}
                  disableElevation
                >
                  Cerrar
                </Button>
              ) : (
                <></>
              )}
            </Grid>
          </Grid>
        </Grid>
      </AppForm>
      <AppDialog
        open={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        title="Confirme"
        message="¿Está seguro de eliminar el registro?"
        buttons={
          <>
            <Button
              color="secondary"
              variant="contained"
              disableElevation
              onClick={confirmDeletion}
            >
              Sí
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={() => setShowConfirmDelete(false)}
            >
              No
            </Button>
          </>
        }
      />
      <AppDialog
        open={showConfirmActivate}
        onClose={() => setShowConfirmActivate(false)}
        title="Confirme"
        message="¿Está seguro de reactivar el registro?"
        buttons={
          <>
            <Button
              color="secondary"
              variant="contained"
              disableElevation
              onClick={confirmActivation}
            >
              Sí
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={() => setShowConfirmActivate(false)}
            >
              No
            </Button>
          </>
        }
      />
    </>
  );
};

export default EditForm;
